<template>
  <div class="ti">
    <header class="ti-head">
      <div>
        <h1 class="ti-title">Factures transporteurs</h1>
        <p class="ti-sub">Suivez et gérez les factures de vos transporteurs : sacs expédiés × tarif par sac.</p>
      </div>
      <button type="button" class="btn btn-primary ti-add" @click="openCreate">
        <i class="ph ph-plus" aria-hidden="true"></i> Ajouter une facture
      </button>
    </header>

    <!-- ── KPIs ─────────────────────────────────────────────────── -->
    <div class="ti-kpis">
      <div class="ti-kpi">
        <span class="ti-kpi-icon tone-info"><i class="ph ph-file-text" aria-hidden="true"></i></span>
        <div><strong>{{ stats.total }}</strong><span>Total des factures</span></div>
      </div>
      <div class="ti-kpi">
        <span class="ti-kpi-icon tone-warning"><i class="ph ph-clock" aria-hidden="true"></i></span>
        <div>
          <strong class="text-warning-strong">{{ stats.unpaid }}</strong>
          <span>Non payée(s) · {{ money(stats.unpaid_amount) }}</span>
        </div>
      </div>
      <div class="ti-kpi">
        <span class="ti-kpi-icon tone-success"><i class="ph ph-check" aria-hidden="true"></i></span>
        <div>
          <strong class="text-success-strong">{{ stats.paid }}</strong>
          <span>Payée(s) · {{ money(stats.paid_amount) }}</span>
        </div>
      </div>
    </div>

    <div class="ti-layout" :class="{ 'has-panel': payTarget }">
      <!-- ── List ─────────────────────────────────────────────────── -->
      <section class="ti-card">
        <div class="ti-filters">
          <div class="ti-search">
            <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
            <input
              v-model="filters.searchTerm"
              type="search"
              placeholder="Rechercher une facture, un transporteur…"
              aria-label="Rechercher une facture"
              @input="onSearch"
            />
          </div>
          <select v-model="filters.status" class="form-select ti-select" aria-label="Statut" @change="fetchInvoices(1)">
            <option value="">Tous les statuts</option>
            <option value="unpaid">Non payées</option>
            <option value="paid">Payées</option>
          </select>
        </div>

        <div class="table-responsive ti-table-wrap">
          <table class="table ti-table align-middle mb-0">
            <thead>
              <tr>
                <th>N° facture</th>
                <th>Transporteur</th>
                <th class="text-center">Sacs</th>
                <th class="text-end">Montant</th>
                <th>Statut</th>
                <th class="text-center">Actions</th>
                <th style="width: 44px"><span class="visually-hidden">Menu</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="text-center py-4"><span class="spinner-border spinner-border-sm"></span></td>
              </tr>
              <tr v-else-if="!invoices.length">
                <td colspan="7" class="ti-empty">
                  <i class="ph ph-receipt" aria-hidden="true"></i>
                  Aucune facture transporteur.
                </td>
              </tr>
              <tr v-for="inv in invoices" v-else :key="inv.id" :class="{ 'is-current': payTarget?.id === inv.id }">
                <td><strong>{{ inv.ref }}</strong></td>
                <td>{{ inv.transport_name }}</td>
                <td class="text-center">{{ inv.sacs }}</td>
                <td class="text-end text-nowrap">{{ money(inv.amount) }}</td>
                <td>
                  <span v-if="inv.status === 'paid'" class="ti-badge tone-success">
                    <i class="ph-fill ph-check-circle" aria-hidden="true"></i> Payée
                  </span>
                  <span v-else class="ti-badge tone-warning">
                    <i class="ph ph-clock" aria-hidden="true"></i> Non payée
                  </span>
                </td>
                <td class="text-center">
                  <button v-if="inv.status !== 'paid'" type="button" class="btn btn-sm btn-primary ti-act" @click="openPay(inv)">
                    Payer
                  </button>
                  <template v-else>
                    <button type="button" class="btn btn-sm btn-outline-secondary ti-act" @click="openDetails(inv)">
                      Voir détails
                    </button>
                    <span class="ti-mode-tag">{{ modeLabel(inv.payment_mode) }}</span>
                  </template>
                </td>
                <td class="text-end">
                  <div class="dropdown">
                    <button
                      type="button"
                      class="ti-kebab"
                      data-bs-toggle="dropdown"
                      data-bs-popper-config='{"strategy":"fixed"}'
                      aria-expanded="false"
                      :aria-label="`Actions ${inv.ref}`"
                    >
                      <i class="ph-bold ph-dots-three-vertical" aria-hidden="true"></i>
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li>
                        <button type="button" class="dropdown-item" @click="openDetails(inv)">
                          <i class="ph ph-eye me-5" aria-hidden="true"></i>Voir détails
                        </button>
                      </li>
                      <li>
                        <button type="button" class="dropdown-item" @click="openPdf(inv)">
                          <i class="ph ph-file-pdf me-5" aria-hidden="true"></i>PDF
                        </button>
                      </li>
                      <li v-if="inv.status !== 'paid'">
                        <button type="button" class="dropdown-item" @click="openPay(inv)">
                          <i class="ph ph-wallet me-5" aria-hidden="true"></i>Payer
                        </button>
                      </li>
                      <li v-if="inv.status !== 'paid'"><hr class="dropdown-divider" /></li>
                      <li v-if="inv.status !== 'paid'">
                        <button type="button" class="dropdown-item text-danger" @click="remove(inv)">
                          <i class="ph ph-trash me-5" aria-hidden="true"></i>Supprimer
                        </button>
                      </li>
                    </ul>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="pagination.total" class="ti-pager">
          <span>{{ pagination.from || 0 }} à {{ pagination.to || 0 }} sur {{ pagination.total }} résultat(s)</span>
          <div class="btn-group">
            <button type="button" class="btn btn-sm btn-light" :disabled="pagination.current_page <= 1" aria-label="Page précédente" @click="fetchInvoices(pagination.current_page - 1)">
              <i class="ph ph-caret-left" aria-hidden="true"></i>
            </button>
            <span class="btn btn-sm btn-primary disabled">{{ pagination.current_page }}</span>
            <button type="button" class="btn btn-sm btn-light" :disabled="pagination.current_page >= pagination.last_page" aria-label="Page suivante" @click="fetchInvoices(pagination.current_page + 1)">
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </section>

      <!-- ── Side panel: payment + last paid invoice ─────────────── -->
      <aside class="ti-aside">
        <form v-if="payTarget" ref="payPanel" class="ti-card ti-pay" novalidate @submit.prevent="pay">
          <header class="ti-pay-head">
            <h2>Payer la facture — {{ payTarget.ref }}</h2>
            <button type="button" class="btn-close" aria-label="Fermer" @click="closePay"></button>
          </header>

          <div class="ti-pay-sum">
            <div>
              <small>Transporteur</small>
              <strong>{{ payTarget.transport_name }}</strong>
            </div>
            <div class="ti-pay-amount">
              <small>Montant à payer</small>
              <strong>{{ money(payTarget.amount) }}</strong>
            </div>
          </div>

          <label class="ti-label" id="ti-mode-label">Mode de paiement</label>
          <div class="ti-modes" role="radiogroup" aria-labelledby="ti-mode-label">
            <button
              v-for="m in modes"
              :key="m.value"
              type="button"
              role="radio"
              class="ti-mode"
              :class="{ 'is-active': payForm.payment_mode === m.value }"
              :aria-checked="payForm.payment_mode === m.value"
              @click="setMode(m.value)"
            >
              <span class="ti-radio" aria-hidden="true"></span>
              <i class="ph" :class="m.icon" aria-hidden="true"></i>
              <strong>{{ m.label }}</strong>
              <small>{{ m.help }}</small>
            </button>
          </div>
          <span v-if="payErrors.payment_mode" class="ti-err">{{ payErrors.payment_mode }}</span>

          <div class="ti-pay-fields">
            <div>
              <label for="ti-pay-date" class="ti-label">Date de paiement</label>
              <div class="ti-input-icon">
                <i class="ph ph-calendar-blank" aria-hidden="true"></i>
                <input id="ti-pay-date" v-model="payForm.payment_date" type="date" class="form-control" :class="{ 'is-invalid': payErrors.payment_date }" />
              </div>
              <span v-if="payErrors.payment_date" class="ti-err">{{ payErrors.payment_date }}</span>
            </div>
            <div>
              <label for="ti-pay-ref" class="ti-label">Référence du virement</label>
              <div class="ti-input-icon">
                <i class="ph ph-file-text" aria-hidden="true"></i>
                <input
                  id="ti-pay-ref"
                  v-model="payForm.payment_reference"
                  type="text"
                  maxlength="100"
                  class="form-control"
                  :class="{ 'is-invalid': payErrors.payment_reference }"
                  :disabled="payForm.payment_mode !== 'transfer'"
                  placeholder="Ex. VIR-0001"
                />
              </div>
              <span v-if="payErrors.payment_reference" class="ti-err">{{ payErrors.payment_reference }}</span>
            </div>
          </div>
          <small class="ti-hint">
            <i class="ph ph-info" aria-hidden="true"></i>
            La référence du virement apparaît uniquement pour un paiement par virement.
          </small>

          <p class="ti-info">
            <i class="ph-fill ph-info" aria-hidden="true"></i>
            <span>Après confirmation, la facture passe à <strong>Payée</strong>.</span>
          </p>

          <div class="ti-pay-actions">
            <button type="button" class="btn btn-light" @click="closePay">Annuler</button>
            <button type="submit" class="btn btn-primary" :disabled="paying">
              <span v-if="paying" class="spinner-border spinner-border-sm me-5" role="status"></span>
              Confirmer le paiement
            </button>
          </div>
        </form>

        <div v-else class="ti-card ti-pay-empty">
          <i class="ph ph-hand-pointing" aria-hidden="true"></i>
          <p>Cliquez sur <strong>Payer</strong> dans la liste pour régler une facture.</p>
        </div>

        <div v-if="lastPaid" class="ti-card ti-last">
          <header>
            <h3>Dernière facture payée — {{ lastPaid.ref }}</h3>
            <span class="ti-badge tone-success"><i class="ph-fill ph-check-circle" aria-hidden="true"></i> Payée</span>
          </header>
          <dl>
            <dt><i class="ph ph-truck" aria-hidden="true"></i> Transporteur :</dt>
            <dd>{{ lastPaid.transport_name }}</dd>
            <dt><i class="ph ph-money" aria-hidden="true"></i> Mode :</dt>
            <dd>{{ modeLabel(lastPaid.payment_mode) }}<template v-if="lastPaid.payment_reference"> · {{ lastPaid.payment_reference }}</template></dd>
            <dt><i class="ph ph-calendar-blank" aria-hidden="true"></i> Date :</dt>
            <dd>{{ date(lastPaid.payment_date) }}</dd>
            <dt><i class="ph ph-coins" aria-hidden="true"></i> Montant :</dt>
            <dd>{{ money(lastPaid.amount) }}</dd>
          </dl>
          <button type="button" class="ti-link" @click="openDetails(lastPaid)">
            <i class="ph ph-clock-counter-clockwise" aria-hidden="true"></i> Historique
          </button>
        </div>
      </aside>
    </div>

    <!-- ── Create modal ─────────────────────────────────────────── -->
    <div ref="createEl" class="modal fade" tabindex="-1" aria-labelledby="tiCreateTitle" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h2 id="tiCreateTitle" class="modal-title fs-5">Ajouter une facture</h2>
              <small class="text-primary">Transporteurs avec des sacs expédiés non encore facturés.</small>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <div class="ti-search ti-search-wide mb-15">
              <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
              <input
                v-model="createSearch"
                type="search"
                placeholder="Rechercher un transporteur…"
                aria-label="Rechercher un transporteur"
                @input="onCreateSearch"
              />
            </div>

            <div class="row g-2 align-items-end mb-15">
              <div class="col-sm-4">
                <label for="ti-date-from" class="ti-label">Expédié du</label>
                <input id="ti-date-from" v-model="dateFrom" type="date" class="form-control" :max="dateTo || undefined" @change="loadTransports" />
              </div>
              <div class="col-sm-4">
                <label for="ti-date-to" class="ti-label">au</label>
                <input id="ti-date-to" v-model="dateTo" type="date" class="form-control" :min="dateFrom || undefined" @change="loadTransports" />
              </div>
              <div class="col-sm-4">
                <button v-if="dateFrom || dateTo" type="button" class="btn btn-light" @click="resetDates">
                  <i class="ph ph-x me-5" aria-hidden="true"></i>Réinitialiser les dates
                </button>
              </div>
            </div>

            <div v-if="loadingUninvoiced && !transports.length" class="text-center py-4"><span class="spinner-border spinner-border-sm"></span></div>
            <div v-else-if="!transports.length" class="ti-empty-box">
              <i class="ph ph-truck" aria-hidden="true"></i>
              {{ createSearch || dateFrom || dateTo ? "Aucun bon ne correspond aux filtres." : "Aucun bon d'envoi expédié à facturer." }}
            </div>
            <template v-else>
              <div class="table-responsive ti-bordered">
                <table class="table ti-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th style="width: 90px">Sélection</th>
                      <th>Transporteur</th>
                      <th class="text-center">Nombre de bons</th>
                      <th class="text-center">Nombre de sacs</th>
                      <th class="text-end">Total à payer</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="t in transports"
                      :key="t.transport_id"
                      class="ti-pick"
                      :class="{ 'is-selected': selectedId === t.transport_id }"
                      @click="selectTransport(t.transport_id)"
                    >
                      <td class="text-center">
                        <input
                          class="form-check-input"
                          type="radio"
                          name="ti-transport"
                          :checked="selectedId === t.transport_id"
                          :aria-label="`Sélectionner ${t.transport_name}`"
                          @change="selectTransport(t.transport_id)"
                        />
                      </td>
                      <td><strong>{{ t.transport_name }}</strong></td>
                      <td class="text-center">{{ t.notes_count }}</td>
                      <td class="text-center">{{ t.sacs }}</td>
                      <td class="text-end fw-bold">{{ money(t.amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="selected" class="ti-detail mt-20">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-10">
                  <h3 class="mb-0">Bons d'envoi de {{ selected.transport_name }}</h3>
                  <div class="d-flex align-items-center gap-3">
                    <small class="text-muted">
                      <strong>{{ selection.count }}</strong> / {{ selected.notes_count }} bon(s) sélectionné(s)
                    </small>
                    <button
                      v-if="selection.count < selected.notes_count"
                      type="button"
                      class="ti-link"
                      @click="setAllMatching(true)"
                    >
                      Tout sélectionner ({{ selected.notes_count }})
                    </button>
                    <button v-else type="button" class="ti-link" @click="setAllMatching(false)">Tout désélectionner</button>
                  </div>
                </div>
                <div class="table-responsive" :class="{ 'opacity-50': loadingNotes }">
                  <table class="table ti-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th style="width: 40px">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="pageAllChecked"
                            aria-label="Sélectionner les bons de cette page"
                            @change="togglePage($event.target.checked)"
                          />
                        </th>
                        <th>Référence</th>
                        <th>Statut</th>
                        <th>Expédié le</th>
                        <th>Zone</th>
                        <th class="text-center">Sacs</th>
                        <th class="text-end">Tarif par sac</th>
                        <th class="text-end">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loadingNotes && !notes.length">
                        <td colspan="8" class="text-center py-3"><span class="spinner-border spinner-border-sm"></span></td>
                      </tr>
                      <tr v-for="n in notes" :key="n.id" :class="{ 'is-selected': isChecked(n) }">
                        <td>
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="isChecked(n)"
                            :aria-label="`Sélectionner ${n.note_ref}`"
                            @change="setNote(n, $event.target.checked)"
                          />
                        </td>
                        <td>{{ n.note_ref }}</td>
                        <td><span class="ti-badge" :class="noteStatus(n).tone">{{ noteStatus(n).label }}</span></td>
                        <td>{{ date(n.sent_time) }}</td>
                        <td>{{ n.zone || "—" }}</td>
                        <td class="text-center">{{ n.sacs }}</td>
                        <td class="text-end">{{ n.price_per_bag !== null ? money(n.price_per_bag) : "—" }}</td>
                        <td class="text-end fw-bold">{{ money(n.amount) }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="ti-total">
                        <td colspan="5">Total sélectionné ({{ selection.count }} bon(s))</td>
                        <td colspan="3" class="text-end">{{ selection.sacs }} sacs — {{ money(selection.amount) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div v-if="notesPager.total" class="ti-pager">
                  <span>{{ notesPager.from || 0 }} à {{ notesPager.to || 0 }} sur {{ notesPager.total }} bon(s)</span>
                  <div class="btn-group">
                    <button type="button" class="btn btn-sm btn-light" :disabled="loadingNotes || notesPager.current_page <= 1" aria-label="Page précédente" @click="loadNotes(notesPager.current_page - 1)">
                      <i class="ph ph-caret-left" aria-hidden="true"></i>
                    </button>
                    <span class="btn btn-sm btn-primary disabled">{{ notesPager.current_page }} / {{ notesPager.last_page }}</span>
                    <button type="button" class="btn btn-sm btn-light" :disabled="loadingNotes || notesPager.current_page >= notesPager.last_page" aria-label="Page suivante" @click="loadNotes(notesPager.current_page + 1)">
                      <i class="ph ph-caret-right" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <p class="ti-info mt-15 mb-0">
                  <i class="ph ph-info" aria-hidden="true"></i>
                  Données issues des bons d'envoi expédiés. Les sacs déjà facturés sont exclus. La sélection s'applique à toutes les pages.
                </p>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
            <button type="button" class="btn btn-primary" :disabled="!selected || selection.count <= 0 || creating" @click="createInvoice">
              <span v-if="creating" class="spinner-border spinner-border-sm me-5" role="status"></span>
              Créer la facture{{ selected ? ` — ${selected.transport_name}` : "" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Details modal ────────────────────────────────────────── -->
    <div ref="detailsEl" class="modal fade" tabindex="-1" aria-labelledby="tiDetailsTitle" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h2 id="tiDetailsTitle" class="modal-title fs-5">
              Facture {{ details?.ref }}
              <span v-if="details" class="ti-badge ms-5" :class="details.status === 'paid' ? 'tone-success' : 'tone-warning'">
                {{ details.status === "paid" ? "Payée" : "Non payée" }}
              </span>
            </h2>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingDetails" class="text-center py-4"><span class="spinner-border spinner-border-sm"></span></div>
            <template v-else-if="details">
              <dl class="ti-dl">
                <dt><i class="ph ph-truck" aria-hidden="true"></i> Transporteur</dt>
                <dd>{{ details.transport_name }}</dd>
                <dt><i class="ph ph-coins" aria-hidden="true"></i> Montant</dt>
                <dd class="fw-bold">{{ money(details.amount) }} ({{ details.sacs }} sacs, {{ details.notes_count }} bon(s))</dd>
                <dt><i class="ph ph-calendar-plus" aria-hidden="true"></i> Créée le</dt>
                <dd>{{ date(details.created_at) }}<template v-if="details.created_by_name"> par {{ details.created_by_name }}</template></dd>
                <template v-if="details.status === 'paid'">
                  <dt><i class="ph ph-wallet" aria-hidden="true"></i> Mode</dt>
                  <dd>{{ modeLabel(details.payment_mode) }}</dd>
                  <dt><i class="ph ph-calendar-check" aria-hidden="true"></i> Date de paiement</dt>
                  <dd>{{ date(details.payment_date) }}<template v-if="details.paid_by_name"> par {{ details.paid_by_name }}</template></dd>
                  <template v-if="details.payment_reference">
                    <dt><i class="ph ph-hash" aria-hidden="true"></i> Référence</dt>
                    <dd>{{ details.payment_reference }}</dd>
                  </template>
                </template>
              </dl>
              <div class="table-responsive ti-bordered">
                <table class="table ti-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Référence</th>
                      <th>Expédié le</th>
                      <th>Zone</th>
                      <th class="text-center">Sacs</th>
                      <th class="text-end">Tarif par sac</th>
                      <th class="text-end">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="n in details.notes" :key="n.id">
                      <td>{{ n.note_ref }}</td>
                      <td>{{ date(n.sent_time) }}</td>
                      <td>{{ n.zone || "—" }}</td>
                      <td class="text-center">{{ n.sacs }}</td>
                      <td class="text-end">{{ n.price_per_bag !== null ? money(n.price_per_bag) : "—" }}</td>
                      <td class="text-end fw-bold">{{ money(n.amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Fermer</button>
            <button v-if="details" type="button" class="btn btn-outline-secondary" @click="openPdf(details)">
              <i class="ph ph-file-pdf me-5" aria-hidden="true"></i>PDF
            </button>
            <button v-if="details && details.status !== 'paid'" type="button" class="btn btn-primary" @click="payFromDetails">Payer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import api from "@/utils/axios";
import { currency } from "@/utils/currency";
import { useAppToast } from "@/composables/useAppToast";

interface NoteRow {
  id: number;
  note_ref: string;
  sent_time: string | null;
  delivered: number;
  sacs: number;
  zone: string | null;
  price_per_bag: number | null;
  amount: number;
}
interface UninvoicedTransport {
  transport_id: number;
  transport_name: string;
  notes_count: number;
  sacs: number;
  amount: number;
}
interface Invoice {
  id: number;
  ref: string;
  transport_id: number;
  transport_name: string;
  notes_count: number;
  sacs: number;
  amount: number;
  status: "paid" | "unpaid";
  payment_mode: "cash" | "transfer" | null;
  payment_date: string | null;
  payment_reference: string | null;
  created_at: string;
  created_by_name?: string | null;
  paid_by_name?: string | null;
  notes?: NoteRow[];
}

const modes = [
  { value: "cash", label: "Espèces", help: "Paiement en espèces", icon: "ph-money" },
  { value: "transfer", label: "Virement", help: "Virement bancaire", icon: "ph-bank" },
] as const;

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default defineComponent({
  name: "TransportInvoices",
  setup() {
    const { showToast } = useAppToast();

    const money = (v: unknown) =>
      Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ` ${currency()}`;
    const date = (v: string | null | undefined) => {
      if (!v) return "—";
      const d = new Date(String(v).length === 10 ? `${v}T00:00:00` : String(v).replace(" ", "T"));
      return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("fr-FR");
    };
    const modeLabel = (m: string | null) => modes.find((x) => x.value === m)?.label || "—";
    const noteStatus = (n: NoteRow) =>
      Number(n.delivered) === 1 ? { label: "Reçu", tone: "tone-info" } : { label: "Expédié", tone: "tone-success" };

    // ── List ──
    const invoices = ref<Invoice[]>([]);
    const loading = ref(true);
    const stats = reactive({ total: 0, unpaid: 0, paid: 0, unpaid_amount: 0, paid_amount: 0 });
    const filters = reactive({ searchTerm: "", status: "" });
    const pagination = reactive({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0, per_page: 10 });
    const lastPaid = ref<Invoice | null>(null);

    async function fetchInvoices(page = 1) {
      loading.value = true;
      try {
        const res = await api.get("/api/auth/transport_invoices", {
          params: { page, per_page: pagination.per_page, searchTerm: filters.searchTerm || undefined, status: filters.status || undefined },
        });
        const p = res.data.data;
        invoices.value = p.data || [];
        Object.assign(pagination, {
          current_page: p.current_page,
          last_page: p.last_page,
          total: p.total,
          from: p.from,
          to: p.to,
        });
        Object.assign(stats, res.data.stats || {});
        lastPaid.value = res.data.last_paid || null;
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Impossible de charger les factures.", "error");
      } finally {
        loading.value = false;
      }
    }
    let searchTimer: ReturnType<typeof setTimeout> | undefined;
    const onSearch = () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => fetchInvoices(1), 350);
    };
    onMounted(() => fetchInvoices());

    // ── Modals ──
    const createEl = ref<HTMLElement | null>(null);
    const payPanel = ref<HTMLElement | null>(null);
    const detailsEl = ref<HTMLElement | null>(null);
    const modal = (el: HTMLElement | null) => (el ? Modal.getOrCreateInstance(el) : null);

    // ── Create ──
    // Server-side filtering + pagination: the modal never holds more than one page of notes.
    //  - transports: one aggregated row per transport (count / bags / amount) for the filters;
    //  - notes: one page of the selected transport's notes;
    //  - selection: "all matching" minus the unticked notes (selectAll) or only the ticked ones,
    //    so a selection can span pages without sending or loading every id.
    const transports = ref<UninvoicedTransport[]>([]);
    const loadingUninvoiced = ref(false);
    const createSearch = ref("");
    const selectedId = ref<number | null>(null);
    const creating = ref(false);
    const dateFrom = ref("");
    const dateTo = ref("");

    const notes = ref<NoteRow[]>([]);
    const loadingNotes = ref(false);
    const notesPager = reactive({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0, per_page: 10 });

    type Picked = Record<number, { sacs: number; amount: number }>;
    const selectAll = ref(true);
    const excluded = ref<Picked>({}); // selectAll: unticked notes
    const included = ref<Picked>({}); // !selectAll: ticked notes
    type Sum = { count: number; sacs: number; amount: number };
    const sumOf = (p: Picked) =>
      Object.values(p).reduce<Sum>((a, n) => ({ count: a.count + 1, sacs: a.sacs + n.sacs, amount: a.amount + n.amount }), {
        count: 0,
        sacs: 0,
        amount: 0,
      });

    const selected = computed(() => transports.value.find((t) => t.transport_id === selectedId.value) || null);
    const isChecked = (n: NoteRow) => (selectAll.value ? !(n.id in excluded.value) : n.id in included.value);
    const selection = computed(() => {
      const t = selected.value;
      if (!t) return { count: 0, sacs: 0, amount: 0 };
      if (!selectAll.value) return sumOf(included.value);
      const ex = sumOf(excluded.value);
      return { count: t.notes_count - ex.count, sacs: t.sacs - ex.sacs, amount: t.amount - ex.amount };
    });
    const pageAllChecked = computed(() => notes.value.length > 0 && notes.value.every(isChecked));

    function setNote(n: NoteRow, on: boolean) {
      const target = selectAll.value ? excluded : included;
      const next = { ...target.value };
      // in "all" mode ticking removes the exclusion; otherwise ticking adds the note
      if (on === selectAll.value) delete next[n.id];
      else next[n.id] = { sacs: n.sacs, amount: n.amount };
      target.value = next;
    }
    const togglePage = (on: boolean) => notes.value.forEach((n) => setNote(n, on));
    function setAllMatching(on: boolean) {
      selectAll.value = on;
      excluded.value = {};
      included.value = {};
    }

    // Stale responses (fast typing / paging) are ignored
    let transportsSeq = 0;
    let notesSeq = 0;
    const filterParams = () => ({ date_from: dateFrom.value || undefined, date_to: dateTo.value || undefined });

    async function loadNotes(page = 1) {
      if (selectedId.value === null) {
        notes.value = [];
        Object.assign(notesPager, { current_page: 1, last_page: 1, total: 0, from: 0, to: 0 });
        return;
      }
      const seq = ++notesSeq;
      loadingNotes.value = true;
      try {
        const res = await api.get(`/api/auth/transport_invoices/uninvoiced/${selectedId.value}/notes`, {
          params: { ...filterParams(), page, per_page: notesPager.per_page },
        });
        if (seq !== notesSeq) return;
        const p = res.data.data;
        notes.value = p.data || [];
        Object.assign(notesPager, { current_page: p.current_page, last_page: p.last_page, total: p.total, from: p.from, to: p.to });
      } catch (error: any) {
        if (seq === notesSeq) showToast(error?.response?.data?.message || "Impossible de charger les bons.", "error");
      } finally {
        if (seq === notesSeq) loadingNotes.value = false;
      }
    }

    // Filters changed: reload the transport totals, restart the selection on the first transport kept
    async function loadTransports() {
      const seq = ++transportsSeq;
      loadingUninvoiced.value = true;
      try {
        const res = await api.get("/api/auth/transport_invoices/uninvoiced", {
          params: { ...filterParams(), searchTerm: createSearch.value.trim() || undefined },
        });
        if (seq !== transportsSeq) return;
        transports.value = res.data.data || [];
        if (!transports.value.some((t) => t.transport_id === selectedId.value)) {
          selectedId.value = transports.value.length ? transports.value[0].transport_id : null;
        }
        setAllMatching(true);
        await loadNotes(1);
      } catch (error: any) {
        if (seq === transportsSeq) showToast(error?.response?.data?.message || "Impossible de charger les bons à facturer.", "error");
      } finally {
        if (seq === transportsSeq) loadingUninvoiced.value = false;
      }
    }

    let createSearchTimer: ReturnType<typeof setTimeout> | undefined;
    const onCreateSearch = () => {
      clearTimeout(createSearchTimer);
      createSearchTimer = setTimeout(loadTransports, 350);
    };
    function resetDates() {
      dateFrom.value = "";
      dateTo.value = "";
      loadTransports();
    }
    function selectTransport(id: number) {
      if (id === selectedId.value) return;
      selectedId.value = id;
      setAllMatching(true);
      loadNotes(1);
    }

    function openCreate() {
      createSearch.value = "";
      dateFrom.value = "";
      dateTo.value = "";
      selectedId.value = null;
      transports.value = [];
      notes.value = [];
      modal(createEl.value)?.show();
      loadTransports();
    }

    async function createInvoice() {
      if (!selected.value || selection.value.count <= 0 || creating.value) return;
      creating.value = true;
      try {
        const res = await api.post("/api/auth/transport_invoices", {
          transport_id: selected.value.transport_id,
          ...filterParams(),
          ...(selectAll.value
            ? { select_all: true, excluded_ids: Object.keys(excluded.value).map(Number) }
            : { note_ids: Object.keys(included.value).map(Number) }),
        });
        if (res.data?.success) {
          modal(createEl.value)?.hide();
          showToast(`Facture ${res.data.data.ref} créée.`, "success");
          fetchInvoices(1);
        } else {
          showToast(res.data?.message || "La création a échoué.", "error");
        }
      } catch (error: any) {
        showToast(error?.response?.data?.message || "La création a échoué.", "error");
      } finally {
        creating.value = false;
      }
    }

    // ── Pay ──
    const payTarget = ref<Invoice | null>(null);
    const payForm = reactive({ payment_mode: "cash", payment_date: today(), payment_reference: "" });
    const payErrors = reactive<Record<string, string>>({});
    const paying = ref(false);

    // Payment happens in the side panel (next to the list, below it on small screens)
    async function openPay(inv: Invoice) {
      payTarget.value = inv;
      Object.assign(payForm, { payment_mode: "cash", payment_date: today(), payment_reference: "" });
      Object.keys(payErrors).forEach((k) => delete payErrors[k]);
      await nextTick();
      payPanel.value?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    function closePay() {
      payTarget.value = null;
    }
    function setMode(mode: string) {
      payForm.payment_mode = mode;
      delete payErrors.payment_mode;
      if (mode !== "transfer") {
        payForm.payment_reference = "";
        delete payErrors.payment_reference;
      }
    }
    function payFromDetails() {
      const inv = details.value;
      modal(detailsEl.value)?.hide();
      if (inv) openPay(inv);
    }

    async function pay() {
      Object.keys(payErrors).forEach((k) => delete payErrors[k]);
      if (!payForm.payment_mode) payErrors.payment_mode = "Choisissez le mode de paiement.";
      if (!payForm.payment_date) payErrors.payment_date = "La date de paiement est obligatoire.";
      if (payForm.payment_mode === "transfer" && !payForm.payment_reference.trim())
        payErrors.payment_reference = "La référence du virement est obligatoire.";
      if (Object.keys(payErrors).length || paying.value || !payTarget.value) return;

      paying.value = true;
      try {
        const res = await api.post(`/api/auth/transport_invoices/${payTarget.value.id}/pay`, {
          payment_mode: payForm.payment_mode,
          payment_date: payForm.payment_date,
          payment_reference: payForm.payment_mode === "transfer" ? payForm.payment_reference.trim() : null,
        });
        if (res.data?.success) {
          showToast(`Facture ${payTarget.value.ref} payée.`, "success");
          payTarget.value = null;
          fetchInvoices(pagination.current_page);
        } else {
          showToast(res.data?.message || "Le paiement a échoué.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        Object.keys(errs).forEach((k) => (payErrors[k] = errs[k][0]));
        showToast(error?.response?.data?.message || "Le paiement a échoué.", "error");
      } finally {
        paying.value = false;
      }
    }

    // ── Details ──
    const details = ref<Invoice | null>(null);
    const loadingDetails = ref(false);
    async function openDetails(inv: Invoice) {
      details.value = inv;
      modal(detailsEl.value)?.show();
      loadingDetails.value = true;
      try {
        const res = await api.get(`/api/auth/transport_invoices/${inv.id}`);
        details.value = res.data.data;
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Impossible de charger la facture.", "error");
      } finally {
        loadingDetails.value = false;
      }
    }

    // ── PDF ──
    async function openPdf(inv: Invoice) {
      try {
        const res = await api.get(`/api/auth/transport_invoices/${inv.id}/pdf`, { responseType: "arraybuffer" });
        const blob = new Blob([res.data], { type: "application/pdf" });
        window.open(window.URL.createObjectURL(blob), "_blank");
      } catch {
        showToast("Impossible de générer le PDF de la facture.", "error");
      }
    }

    // ── Delete (unpaid only: its notes can be invoiced again) ──
    function remove(inv: Invoice) {
      Swal.fire({
        title: "Supprimer cette facture ?",
        text: `« ${inv.ref} » sera supprimée ; ses ${inv.notes_count} bon(s) pourront être refacturés.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#E44A4A",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        reverseButtons: true,
      }).then(async (result: any) => {
        if (!result.isConfirmed) return;
        try {
          const res = await api.post(`/api/auth/transport_invoices/${inv.id}/delete`);
          if (res.data?.success) {
            showToast("Facture supprimée.", "success");
            if (payTarget.value?.id === inv.id) payTarget.value = null;
            fetchInvoices(pagination.current_page);
          }
        } catch (error: any) {
          showToast(error?.response?.data?.message || "Erreur lors de la suppression.", "error");
        }
      });
    }

    onBeforeUnmount(() => {
      clearTimeout(searchTimer);
      clearTimeout(createSearchTimer);
      [createEl, detailsEl].forEach((el) => el.value && Modal.getInstance(el.value)?.dispose());
    });

    return {
      modes,
      money,
      date,
      modeLabel,
      noteStatus,
      invoices,
      loading,
      stats,
      filters,
      pagination,
      fetchInvoices,
      onSearch,
      createEl,
      payPanel,
      detailsEl,
      lastPaid,
      closePay,
      setMode,
      transports,
      loadingUninvoiced,
      createSearch,
      selectedId,
      selected,
      dateFrom,
      dateTo,
      notes,
      loadingNotes,
      notesPager,
      selectAll,
      selection,
      pageAllChecked,
      isChecked,
      setNote,
      togglePage,
      setAllMatching,
      loadTransports,
      loadNotes,
      onCreateSearch,
      resetDates,
      selectTransport,
      openPdf,
      creating,
      openCreate,
      createInvoice,
      payTarget,
      payForm,
      payErrors,
      paying,
      openPay,
      payFromDetails,
      pay,
      details,
      loadingDetails,
      openDetails,
      remove,
    };
  },
});
</script>

<style scoped>
.ti {
  padding-bottom: var(--ui-space-6);
}
.ti-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: var(--ui-space-5);
}
.ti-title {
  margin: 0;
  font-size: clamp(1.6rem, 2.6vw, 2.2rem);
  font-weight: var(--ui-fw-black);
  letter-spacing: -0.02em;
}
.ti-sub {
  margin: 6px 0 0;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.ti-add i {
  margin-right: 4px;
}

.ti-kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--ui-space-4);
  margin-bottom: var(--ui-space-5);
}
.ti-kpi {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.ti-kpi strong {
  display: block;
  font-size: 1.6rem;
  font-weight: var(--ui-fw-black);
  line-height: 1.1;
}
.ti-kpi span {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.ti-kpi-icon {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 22px;
}
.text-warning-strong {
  color: var(--ui-warning-strong);
}
.text-success-strong {
  color: var(--ui-success);
}
.tone-info {
  background: var(--ui-info-soft);
  color: var(--ui-info);
}
.tone-warning {
  background: var(--ui-warning-soft);
  color: var(--ui-warning-strong);
}
.tone-success {
  background: var(--ui-success-soft);
  color: var(--ui-success);
}

.ti-card {
  padding: var(--ui-space-5);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-lg);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-card);
}
.ti-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: var(--ui-space-4);
}
.ti-select {
  width: 200px;
  max-width: 100%;
  border-radius: var(--ui-radius-pill);
}
.ti-search {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.ti-search-wide {
  width: 100%;
}
.ti-search i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-faint);
  pointer-events: none;
}
.ti-search input {
  width: 100%;
  height: 40px;
  padding: 0 14px 0 38px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-pill);
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
  font-size: var(--ui-fs-sm);
  outline: none;
}
.ti-search input:focus {
  border-color: var(--ui-accent);
  background: var(--ui-surface);
  box-shadow: var(--ui-focus-ring);
}

.ti-table th {
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-heavy);
  white-space: nowrap;
}
.ti-table td {
  font-size: var(--ui-fs-sm);
}
.ti-bordered {
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
}
.ti-empty,
.ti-empty-box {
  padding: 36px 16px !important;
  color: var(--ui-text-muted);
  text-align: center;
}
.ti-empty i,
.ti-empty-box i {
  display: block;
  margin-bottom: 6px;
  color: var(--ui-accent-text);
  font-size: 28px;
}
.ti-empty-box {
  border: 1px dashed var(--ui-border-strong);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
}

.ti-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: var(--ui-radius-pill);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-bold);
  white-space: nowrap;
}

.ti-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: var(--ui-space-4);
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}

.ti-pick {
  cursor: pointer;
}
.ti-pick.is-selected td {
  background: var(--ui-accent-soft);
}
.ti-detail {
  padding: 16px;
  border: 1px solid rgba(var(--ui-info-rgb), 0.3);
  border-radius: var(--ui-radius-md);
  background: var(--ui-info-soft);
}
.ti-detail h3 {
  margin: 0 0 12px;
  color: var(--ui-info);
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.ti-detail .table-responsive {
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface);
}
.ti-total td {
  background: var(--ui-info-soft);
  color: var(--ui-info);
  font-weight: var(--ui-fw-black);
}
.ti-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-info-soft);
  color: var(--ui-info);
  font-size: var(--ui-fs-sm);
}
.ti-info i {
  font-size: 18px;
}

.ti-pay-sum {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
  padding: 14px 18px;
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface-sunken);
}
.ti-pay-sum small {
  display: block;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
}
.ti-pay-sum strong {
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.ti-amount {
  color: var(--ui-accent-text);
  font-size: 1.4rem !important;
}
.ti-label {
  display: block;
  margin-bottom: 6px;
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
}
.ti-modes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.ti-mode {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 16px 12px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-surface);
  color: var(--ui-text);
  transition: all var(--ui-dur-fast) var(--ui-ease);
}
.ti-mode i {
  color: var(--ui-info);
  font-size: 26px;
}
.ti-mode small {
  color: var(--ui-text-muted);
}
.ti-mode:hover {
  border-color: var(--ui-accent);
}
.ti-mode.is-active {
  border-color: var(--ui-info);
  background: var(--ui-info-soft);
  box-shadow: 0 0 0 1px var(--ui-info);
}
.ti-err {
  display: block;
  margin-top: 6px;
  color: var(--ui-danger);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}

.ti-dl {
  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  gap: 8px 12px;
  margin-bottom: 18px;
}
.ti-dl dt {
  color: var(--ui-text-muted);
  font-weight: var(--ui-fw-semibold);
}
.ti-dl dd {
  margin: 0;
}

/* ── List + side panel ── */
.ti-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: var(--ui-space-5);
  align-items: start;
}
.ti-aside {
  display: flex;
  flex-direction: column;
  gap: var(--ui-space-4);
  position: sticky;
  top: calc(var(--ui-frame-gap, 16px) + var(--ui-topbar-height, 64px) + 16px);
}
.ti-table tbody tr.is-current td {
  background: var(--ui-accent-soft);
}
.ti-act {
  min-width: 104px;
  font-weight: var(--ui-fw-bold);
}
.ti-mode-tag {
  display: block;
  width: fit-content;
  margin: 6px auto 0;
  padding: 2px 10px;
  border-radius: 6px;
  background: var(--ui-surface-sunken);
  color: var(--ui-text-secondary);
  font-size: var(--ui-fs-xs);
  font-weight: var(--ui-fw-semibold);
}
.ti-kebab {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ui-text-secondary);
  font-size: 18px;
}
.ti-kebab:hover,
.ti-kebab[aria-expanded="true"] {
  background: var(--ui-surface-sunken);
  color: var(--ui-text);
}

/* payment panel */
.ti-pay-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}
.ti-pay-head h2 {
  margin: 0;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.ti-pay-amount {
  padding-left: 16px;
  border-left: 1px solid var(--ui-border);
  text-align: right;
}
.ti-pay-amount strong {
  color: var(--ui-accent-text);
  font-size: 1.4rem;
}
.ti-mode {
  position: relative;
}
.ti-radio {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 18px;
  height: 18px;
  border: 2px solid var(--ui-border-strong);
  border-radius: 50%;
  background: var(--ui-surface);
}
.ti-mode.is-active .ti-radio {
  border-color: var(--ui-info);
  box-shadow: inset 0 0 0 3px var(--ui-surface);
  background: var(--ui-info);
}
.ti-pay-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
}
.ti-input-icon {
  position: relative;
}
.ti-input-icon i {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--ui-text-muted);
  pointer-events: none;
}
.ti-input-icon .form-control {
  padding-left: 34px;
}
.ti-hint {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 5px;
  margin: 6px 0 14px;
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-xs);
  text-align: right;
}
.ti-hint i {
  margin-top: 2px;
  color: var(--ui-info);
}
.ti-pay-actions {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 10px;
  margin-top: 14px;
}
.ti-pay-actions .btn {
  font-weight: var(--ui-fw-bold);
}
.ti-pay-empty {
  color: var(--ui-text-muted);
  text-align: center;
}
.ti-pay-empty i {
  color: var(--ui-accent-text);
  font-size: 28px;
}
.ti-pay-empty p {
  margin: 6px 0 0;
  font-size: var(--ui-fs-sm);
}

/* last paid invoice */
.ti-last header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--ui-border);
}
.ti-last h3 {
  margin: 0;
  font-size: var(--ui-fs-md);
  font-weight: var(--ui-fw-black);
}
.ti-last dl {
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  gap: 8px 10px;
  margin: 0;
  font-size: var(--ui-fs-sm);
}
.ti-last dt {
  color: var(--ui-text-muted);
  font-weight: var(--ui-fw-semibold);
}
.ti-last dt i {
  margin-right: 4px;
}
.ti-last dd {
  margin: 0;
}
.ti-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  float: right;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--ui-info);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-bold);
}
.ti-link:hover {
  text-decoration: underline;
}

@media (max-width: 1399.98px) {
  .ti-layout {
    grid-template-columns: minmax(0, 1fr) 360px;
  }
}
@media (max-width: 1199.98px) {
  .ti-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .ti-aside {
    position: static;
  }
  /* on small screens the panel only shows while paying, first */
  .ti-layout.has-panel .ti-aside {
    order: -1;
  }
}
@media (max-width: 767.98px) {
  .ti-pay-fields {
    grid-template-columns: minmax(0, 1fr);
  }
  .ti-kpis {
    grid-template-columns: minmax(0, 1fr);
  }
  .ti-search,
  .ti-select {
    width: 100%;
  }
  .ti-dl {
    grid-template-columns: minmax(0, 1fr);
  }
  .ti-dl dd {
    margin-bottom: 6px;
  }
}
</style>
